// Landing page — platform value proposition with owner/agent CTAs
// Auth is enforced at the SWA platform level — user is always authenticated here
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { brand } from "@/brand";
import { AnimatedLogo } from "@/components/shared/AnimatedLogo";
import {
  Shield,
  EyeOff,
  BadgeCheck,
  QrCode,
  TrendingUp,
  ArrowRight,
  LockKeyhole,
} from "lucide-react";

export function LandingPage() {
  const { user, login, logout, isDevMode } = useAuth();
  const navigate = useNavigate();

  const handleRoleNav = async (role: "owner" | "agent" | "admin") => {
    if (isDevMode) {
      await login(role);
    }
    navigate(`/${role}`);
  };

  return (
    <div className="page-shell">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-white/10 bg-ink/92 backdrop-blur-xl">
        <nav className="mx-auto flex max-w-[1440px] items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
          <AnimatedLogo size={44} />
          <div className="flex items-center gap-3">
            <span className="text-sm text-white/60">
              {(user?.role === "agent" || user?.role === "admin") ? (user?.name || user?.email) : "Anonymous Owner"}
            </span>
            <button onClick={() => handleRoleNav("owner")} className="btn-primary text-sm px-3 py-1.5">
              Owner
            </button>
            <button onClick={() => handleRoleNav("agent")} className="btn-success text-sm px-3 py-1.5">
              Agent
            </button>
            <button onClick={() => handleRoleNav("admin")} className="btn-secondary text-sm px-3 py-1.5">
              Admin
            </button>
            <button onClick={() => logout()} className="btn-ghost text-sm px-3 py-1.5">
              Sign Out
            </button>
          </div>
        </nav>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_82%_10%,rgba(214,168,46,0.18),transparent_28%),linear-gradient(135deg,#07111f_0%,#0d1b2e_52%,#121d2d_100%)]" />
        <div className="relative mx-auto max-w-[1440px] px-4 py-16 sm:px-6 lg:px-8 lg:py-24 text-center">
          <h1 className="text-5xl font-black leading-[0.98] text-white sm:text-6xl lg:text-7xl">
            Connect with trusted agents.
            <br />
            <span className="text-sun">Your details stay private.</span>
          </h1>
          <p className="mt-6 text-xl font-semibold leading-8 text-sun/80 max-w-2xl mx-auto">
            IN COMPLIANCE WITH THE POPI ACT, homeowners privately invite agents to respond to their
            property sale or rental request before consenting to release their contact details.
          </p>
          <div className="mt-10 flex items-center justify-center gap-4">
            <button onClick={() => handleRoleNav("owner")} className="btn-primary px-8 py-3 text-lg">
              I'm a Property Owner <ArrowRight className="h-5 w-5" />
            </button>
            <button onClick={() => handleRoleNav("agent")} className="btn-secondary px-8 py-3 text-lg">
              I'm an Estate Agent
            </button>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="mx-auto max-w-[1440px] px-4 py-16 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-bold text-center text-white mb-12">How it works</h2>
        <div className="grid md:grid-cols-3 gap-8">
          <FeatureCard
            icon={EyeOff}
            title="Create Anonymous Request"
            description="Share your property details — suburb, type, price band, timeline — without revealing your name, address, or phone number."
          />
          <FeatureCard
            icon={BadgeCheck}
            title="Verified Agents Respond"
            description="PPRA-verified agents compete with valuations, market insights, and tailored sale strategies. You compare them privately."
          />
          <FeatureCard
            icon={QrCode}
            title="Unlock on Your Terms"
            description="Choose your preferred agents and release your contact details only when you're ready — via secure QR code with full audit trail."
          />
        </div>
      </section>

      {/* Trust signals */}
      <section className="border-t border-white/10 bg-midnight py-16">
        <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-6 text-center">
            <TrustStat icon={LockKeyhole} value="100%" label="Privacy Protected" />
            <TrustStat icon={BadgeCheck} value="PPRA" label="Verified Agents Only" />
            <TrustStat icon={Shield} value="POPI" label="Act Compliant" />
            <TrustStat icon={TrendingUp} value="70%+" label="Agent Acceptance Rate" />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="mx-auto max-w-[1440px] px-4 py-8 text-center text-muted sm:px-6 lg:px-8">
        <p>© {brand.year} {brand.name}. {brand.description}.</p>
      </footer>
    </div>
  );
}

function FeatureCard({ icon: Icon, title, description }: { icon: React.ElementType; title: string; description: string }) {
  return (
    <div className="card text-center">
      <div className="mx-auto h-12 w-12 rounded-full bg-gold/10 flex items-center justify-center mb-4">
        <Icon className="h-6 w-6 text-sun" />
      </div>
      <h3 className="text-lg font-semibold text-white mb-2">{title}</h3>
      <p className="text-body">{description}</p>
    </div>
  );
}

function TrustStat({ icon: Icon, value, label }: { icon: React.ElementType; value: string; label: string }) {
  return (
    <div className="p-4">
      <Icon className="mx-auto h-8 w-8 text-sun mb-2" />
      <div className="stat-value">{value}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
}
